import {
  OpenFeature,
  type EvaluationContext,
  type JsonValue,
  type Provider,
  type ResolutionDetails,
} from '@openfeature/web-sdk';

/**
 * Trivial config-file provider (backbone §9): flags come from `config.json`, swappable to a
 * commercial provider later without code churn. Flags gate *rollout*; permissions gate
 * *authorization* — agents are told never to substitute one for the other.
 */
export class ConfigFileFlagProvider implements Provider {
  readonly metadata = { name: 'eis-config-file' } as const;
  readonly runsOn = 'client' as const;
  private flags: Readonly<Record<string, boolean>>;

  constructor(flags: Readonly<Record<string, boolean>>) {
    this.flags = flags;
  }

  private resolve<T>(flagKey: string, defaultValue: T): ResolutionDetails<T> {
    const value = this.flags[flagKey];
    if (typeof value === 'boolean' && typeof defaultValue === 'boolean') {
      return { value: value as T, reason: 'STATIC' };
    }
    return { value: defaultValue, reason: 'DEFAULT' };
  }

  resolveBooleanEvaluation(flagKey: string, defaultValue: boolean): ResolutionDetails<boolean> {
    return this.resolve(flagKey, defaultValue);
  }
  resolveStringEvaluation(_flagKey: string, defaultValue: string): ResolutionDetails<string> {
    return { value: defaultValue, reason: 'DEFAULT' };
  }
  resolveNumberEvaluation(_flagKey: string, defaultValue: number): ResolutionDetails<number> {
    return { value: defaultValue, reason: 'DEFAULT' };
  }
  resolveObjectEvaluation<T extends JsonValue>(
    _flagKey: string,
    defaultValue: T,
  ): ResolutionDetails<T> {
    return { value: defaultValue, reason: 'DEFAULT' };
  }

  onContextChange?(_old: EvaluationContext, _next: EvaluationContext): Promise<void> {
    return Promise.resolve();
  }
}

export async function initFlags(flags: Readonly<Record<string, boolean>>): Promise<void> {
  await OpenFeature.setProviderAndWait(new ConfigFileFlagProvider(flags));
}

export function isFlagEnabled(flagKey: string, defaultValue = false): boolean {
  return OpenFeature.getClient().getBooleanValue(flagKey, defaultValue);
}
